import { useMemo } from 'react';
import { layoutSequence, type SequenceLayout } from './layout-sequence';
import { parseMermaidSequence } from './parse-mermaid-sequence';
import {
  SequenceEventKind,
  type MermaidSequence,
  type MermaidSequenceEvent,
} from './sequence-types';

interface SequenceDiagramRenderModel {
  model: MermaidSequence;
  layout: SequenceLayout;
  messageCount: number;
}

function countMessages(events: MermaidSequenceEvent[]): number {
  return events.reduce((sum, event) => {
    if (event.kind === SequenceEventKind.Block) {
      return sum + countMessages(event.events);
    }
    if (event.kind === SequenceEventKind.Message) {
      return sum + 1;
    }
    return sum;
  }, 0);
}

export function useSequenceDiagram(content: string): SequenceDiagramRenderModel | null {
  const model = useMemo(() => parseMermaidSequence(content), [content]);
  const layout = useMemo(() => (model ? layoutSequence(model) : null), [model]);
  const messageCount = useMemo(() => (model ? countMessages(model.events) : 0), [model]);

  return useMemo(() => {
    if (!model || !layout) {
      return null;
    }

    return { model, layout, messageCount };
  }, [layout, messageCount, model]);
}
